/* eslint-disable */
import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { Elements } from 'react-stripe-elements';
import { css, StyleSheet } from 'aphrodite';
import Modal from 'react-modal';

import { Theme } from '../../config/theme';
import { updateAppState } from '../../actions/applicationState';
import { updateUserRemote } from '../../actions/user';

import ErrorContainer from '../ErrorContainer';
import Button from '../Button';
import CheckoutForm from '../CheckoutForm';

const CLOSE_ICON = require('./close.svg');

let styles;

export default class RenewResubscriptionModal extends Component {
  constructor(props) {
    super(props);
    this.handleCardToken = this.handleCardToken.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleErrorReset = this.handleErrorReset.bind(this);
    this.handleErrorTriggered = this.handleErrorTriggered.bind(this);
    this.handleReuseCard = this.handleReuseCard.bind(this);
    this.handleSubmitCard = this.handleSubmitCard.bind(this);
    this.submitPaymentDetails = this.submitPaymentDetails.bind(this);

    const { user = {} } = props;
    const { stripeCard = null, stripeToken = null, last4 = null } = user;

    this.state = {
      errors: {
        card: false,
      },

      // For existing card re-use
      stripeCard,
      stripeToken,
      last4,
    };
  }

  handleClose() {
    this.props.closeHandler();
  }

  handleErrorReset() {
    this.setState({
      errors: {
        card: false,
      }
    });
  }

  /**
   * Handle "use existing card" being checked
   */
  handleReuseCard() {
    this.props.dispatch(updateAppState({
      loading: true,
    }));

    // Update payment details
    this
      .submitPaymentDetails({
        card_token: this.state.stripeCard,
        last4: this.state.last4,
        renew_membership: true,
      })
      .finally(() => {
        this.props.dispatch(updateAppState({
          loading: false,
        }));
      });
  }

  /**
   * Handle "new card" being checked
   *
   * @param {Event} event
   */
  handleSubmitCard() {
    this.props.dispatch(updateAppState({
      loading: true,
    }));

    // Handle reuse or created card response
    this
      .CheckoutForm
      .verifyCard()
      .then((response) => {
        return this.submitPaymentDetails({
          stripe_token: response.token.id,
          card_token: response.token.card.id,
          last4: response.token.card.last4,
          renew_membership: true,
        })
      })
      .catch(this.handleErrorTriggered)
      .finally(() => {
        this.props.dispatch(updateAppState({
          loading: false,
        }));
      });
  }

  submitPaymentDetails(details) {
    return this
      .props
      .dispatch(updateUserRemote(details, this.props.user.access_token))
      .then(() => {
        this.props.closeHandler();
      })
      .catch(this.handleErrorTriggered)
  }

  /**
   * Set errors in the state
   */
  handleErrorTriggered() {
    this.setState({
      errors: Object.assign({}, this.state.errors, {
        card: 'A problem occurred with this card, and you were not charged. Please try another card.',
      }),
      hasError: true,
    });
  }

  handleCardToken(response) {
    return new Promise((resolve, reject) => {
      if (response.error) {
        this.handleErrorTriggered();
        this.props.dispatch(updateAppState({
          loading: false,
        }));

        reject(response);
      } else if (response.token) {
        this.setState({
          stripeToken: response.token.id,
          stripeCard: response.token.card.id,
          last4: response.token.card.last4,
        });

        resolve(response);
      }
    });
  }

  existingPaymentAvailable() {
    // Note: There is a case where a customer lacks a stripe CustomerID. A StripeToken is necessary in order
    // to create a new customer.
    // Thus, if a customerID is not set, we can't re-use an existing payment and must create a new one
    // If card / last4 digits are saved, use these
    return this.props.current_card
      && this.state.stripeCard
      && this.state.last4
      && this.props.user
      && this.props.user.has_customer_id;
  }

  renderExistingPayment() {
    return (
      <div className={css(styles.payment_area)}>
        <Button
          title="Use current payment method"
          styles={styles.full_width_button}
          handler={this.handleReuseCard}/>
        <div className={css(styles.current_card_details)}>
          Current payment method: {this.props.current_card}
        </div>
      </div>
    );
  }

  renderPaymentArea() {
    // Show both "use existing card" and "enter new card" forms
    if (this.existingPaymentAvailable()) {
      return (
        <div className={css(styles.body)}>
          <div className={css(styles.text)}>
            Tails are wagging! Thank you for being us for the time you have and choosing to stay on! To activate your
            resubscription, simply confirm your current preferred payment method or enter a new one.
          </div>
          <hr/>
          {this.renderExistingPayment()}
          <div className={css(styles.seperator_container)}>
            <div className={css(styles.seperator_text)}>
              Or
            </div>
          </div>
          {this.renderNewPayment()}
        </div>
      );
    }

    // Show only "enter new card" form
    return (
      <div className={css(styles.body)}>
        <div className={css(styles.text)}>
          Tails are wagging! Thank you for being us for the time you have and choosing to stay on! To activate your
          resubscription, simply enter a new payment method.
        </div>
        {this.renderNewPayment()}
      </div>
    );
  }

  /**
   * Render "enter new card" form
   *
   * @returns {any}
   */
  renderNewPayment() {
    return (
      <div className={css(styles.payment_area)}>
        <Elements>
          <CheckoutForm
            onRef={ref => (this.CheckoutForm = ref)}
            handler={this.handleCardToken}
            first_name={this.props.first_name}
            last_name={this.props.last_name}
            cardChangedHandler={this.handleErrorReset}/>
        </Elements>
        {this.state.errors.card ?
          <ErrorContainer message={this.state.errors.card}/>
          : null}

        <div className={css(styles.button_container)}>
          <Button
            title="Verify and store card"
            styles={styles.button}
            handler={this.handleSubmitCard}/>
        </div>
      </div>
    );
  }

  render() {
    return (
      <Modal
        isOpen={this.props.open}
        className={css(styles.container)}
        onRequestClose={this.handleClose}
        style={{
          overlay: {
            backgroundColor: 'rgba(0,0,0,0.67)',
            zIndex: 101,
            display: 'flex',
          },
        }}
        contentLabel="Change payment method"
      >
        <div className={css(styles.modal_wrapper)}>
          <div className={css(styles.header)}>
            <h2 className={css(styles.title)}>Activate my resubscription</h2>
            <div
              className={css(styles.close_icon)}
              onClick={this.handleClose}
              role="button"
              tabIndex={0}
            >
              <img src={CLOSE_ICON} alt="Close modal"/>
            </div>
          </div>
          {this.renderPaymentArea()}
        </div>
        <div className={css(styles.footer)}>
          <div className={css(styles.sign_up_container)}>
            <p style={{ fontSize: 14, padding: 20, }}>
              Your account will NOT auto-resubscribe! <br/>
              And if you choose to reactivate your resubscription before your current subscription is up for renewal,
              your card will not be charged until the renewal date of your current subscription.
            </p>
          </div>
        </div>
      </Modal>
    );
  }
}

RenewResubscriptionModal.propTypes = {
  open: PropTypes.bool,
  closeHandler: PropTypes.func,
  first_name: PropTypes.string.isRequired,
  last_name: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  current_card: PropTypes.string,
};

RenewResubscriptionModal.defaultProps = {
  open: false,
  closeHandler: () => {
  },
  current_card: null,
};

styles = StyleSheet.create({
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    width: 590,
    maxWidth: '100vw',
    height: 700,
    maxHeight: '100vh',
    backgroundColor: Theme.white,
    borderRadius: 5,
    overflow: 'hidden',
    outline: 'none',
    margin: 'auto auto',

    [ Theme.mobile_query ]: {
      width: '95%',
      height: 'auto',
    },
  },
  modal_wrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: 600,
    width: '100%',
    boxSizing: 'border-box',
    padding: '0px 80px',

    [ Theme.mobile_query ]: {
      height: 'auto',
      padding: '0 30px',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '50px 0px',
  },
  title: {
    fontFamily: Theme.font_family_heading,
    fontSize: 30,
    fontWeight: '900',
  },
  close_icon: {
    position: 'absolute',
    right: 15,
    top: 15,
    outline: 'none',
    cursor: 'pointer',
    transition: 'opacity 180ms ease-in-out',
    ':hover': {
      opacity: 0.6,
    },
  },
  current_card_details: {
    textAlign: 'center',
    fontFamily: Theme.font_family_heading,
    fontSize: 13,
    marginTop: 5,
  },
  full_width_button: {
    width: '100%',
  },
  payment_area: {
    width: '100%',
  },
  body: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  seperator_container: {
    display: 'flex',
    height: 25,
    margin: '25px auto',
    alignItems: 'center',
    justifyContent: 'center',
    ':after': {
      content: "' '",
      position: 'absolute',
      width: '100%',
      height: 1,
      backgroundColor: Theme.horizontal_rule_color,
    },
  },
  text: {
    fontFamily: Theme.font_family_primary,
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: '21px',
  },
  seperator_text: {
    fontFamily: Theme.font_family_primary,
    backgroundColor: Theme.white,
    zIndex: 10,
    padding: 15,
  },
  actions_container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  footer: {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    bottom: 0,
    height: 100,
    width: '100%',
    backgroundColor: Theme.light_grey,

    [ Theme.mobile_query ]: {
      position: 'relative',
      height: 'auto',
      marginTop: 30,
      padding: '30px',
      textAlign: 'center',
    },
  },
  sign_up_container: {
    fontFamily: Theme.font_family_primary,
    fontSize: 16,
    textAlign: 'center',
  },
  sign_up_link: {
    display: 'inline',
    fontFamily: Theme.font_family_secondary,
    letterSpacing: 1,
    marginLeft: 5,
    textTransform: 'uppercase',
    fontWeight: 'bold',
  },
  forgotten_password: {
    flex: 1,
    fontFamily: Theme.font_family_primary,
    fontSize: 14,
    textAlign: 'center',
  },
  login_button_container: {
    flex: 1,
  },
  clippy: {
    marginLeft: 20,
  },
  copy_button: {
    backgroundColor: 'transparent',
    padding: '10px 20px',
    boxSizing: 'none',
    justifyContent: 'center',
    alignItems: 'center',
    textDecoration: 'none',
    transition: 'all ease-out 160ms',
    cursor: 'pointer',
    border: 'none',
  },
  copy_button_container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: Theme.primary,
    fontSize: 13,
    fontWeight: 'bold',
  },
  button_container: {
    marginTop: 40,

    [ Theme.mobile_query ]: {
      justifyContent: 'center',
    },
  },
});



// WEBPACK FOOTER //
// ./src/components/RenewResubscriptionModal/index.js